import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { ENTITIES, DOC_TYPES, generateRefNumber, generateAddendumRef, buildEmailHTML, buildFolderPath, getNextSequence } from "../../services/contractService";
import { OBLIGATION_CATEGORIES, OBLIGATION_PRIORITIES, RISK_LEVELS, createObligation } from "../../services/obligationService";
import { ownerMappingService } from "../../services/ownerMappingService";
import { SupplierPicker } from "../common/SupplierPicker";
import { generateContractDraft, getTemplateName } from "../../services/templateService";
import { useFormAutosave, getAutosavedFormData, clearAutosavedData } from "../../hooks/useFormAutosave";
// getTemplateName is now async — it queries the TemplateMappings SharePoint list

const Field = ({label, required, children}) => (
  <div className="mb-4.5">
    <label className="block text-base font-medium text-light-text-tertiary dark:text-dark-text-tertiary mb-1.5">
      {label} {required && <span className="text-accent">*</span>}
    </label>
    {children}
  </div>
);

const Input = ({...props}) => (
  <input {...props} className="w-full px-3 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-base font-sans outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors" />
);

const Select = ({options, ...props}) => (
  <select {...props} className="w-full px-3 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-base font-sans outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors">
    <option value="">Select...</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const DEPARTMENTS = [
  "Operations & Asset - Well Engineering",
  "Operations & Asset - Subsurface",
  "Operations & Asset - Production & Operations",
  "Projects & Engineering",
  "Human Resources",
  "General Admin",
  "ESG",
  "Health, Safety, Environment, Security",
  "Supply Chain Management",
  "Legal",
  "Corporate Communications",
  "IT",
  "Finance"
];
const CURRENCIES = ["USD","NGN","GBP","EUR","ZAR","CAD","AED"];
const CONTRACT_TYPES_NEW = ["One-off Service/Order","Monthly Reoccurring Service/Order","Call-Out Service/Order"];

// Suggested doc types per contract type (shown as guidance chips)
const DOC_SUGGESTIONS = {
  "NDA":                          ["contract_draft","executed_contract","correspondence","other"],
  "CRAF":                         ["approval","award_letter","scope_of_work","pricing","other"],
  "Consultancy Service Agreement":["contract_draft","executed_contract","scope_of_work","pricing","kpi","correspondence","other"],
  "Logic Goods":                  ["contract_draft","executed_contract","award_letter","pricing","receipt","completion_cert","insurance","other"],
  "Logic Service":                ["contract_draft","executed_contract","award_letter","scope_of_work","pricing","kpi","completion_cert","receipt","insurance","other"],
  "Standard Goods":               ["contract_draft","executed_contract","award_letter","pricing","receipt","completion_cert","insurance","other"],
  "Standard Service":             ["contract_draft","executed_contract","award_letter","scope_of_work","pricing","kpi","completion_cert","receipt","insurance","other"],
  "Addendum":                     ["addendum","approval","correspondence","other"],
};

const DocTypeGuide = ({ contractType }) => {
  const [open, setOpen] = useState(false);
  const suggested = contractType ? (DOC_SUGGESTIONS[contractType] || []) : [];
  const all = DOC_TYPES;
  const display = open ? all : all.filter(t => suggested.includes(t.id));

  return (
    <div className="mb-3.5 bg-light-bg-secondary dark:bg-dark-bg-tertiary rounded-lg border border-light-border dark:border-dark-border overflow-hidden">
      <button type="button"
        onClick={()=>setOpen(o=>!o)}
        className="w-full flex items-center justify-between px-3.5 py-2 bg-none border-none cursor-pointer gap-2">
        <div className="flex items-center gap-2">
          <span className="text-base text-light-text-secondary dark:text-dark-text-secondary font-medium">Document Types Guide</span>
          {contractType && !open && <span className="text-base text-accent">{suggested.length} suggested for {contractType}</span>}
        </div>
        <span className={`text-base text-light-text-secondary dark:text-dark-text-secondary transition-transform ${open ? "rotate-180" : ""}`}>▼</span>
      </button>
      {(open || suggested.length > 0) && (
        <div className="px-3.5 pb-3 flex flex-wrap gap-1.5">
          {display.map(t => {
            const isSuggested = suggested.includes(t.id);
            return (
              <span key={t.id} style={{
                backgroundColor: isSuggested ? t.color+"22" : "transparent",
                borderColor: isSuggested ? t.color+"55" : "var(--light-border, #d0d4db)",
                color: isSuggested ? t.color : "#4a5a72",
              }} className={`inline-flex items-center gap-1 px-2.5 py-0.75 rounded-full text-base font-medium whitespace-nowrap border ${!isSuggested && "dark:border-dark-border"}`}>
                <span className="font-mono text-xs opacity-80">{t.code}</span>
                {t.label}
              </span>
            );
          })}
          {!open && suggested.length > 0 && (
            <button type="button" onClick={()=>setOpen(true)} className="px-2.5 py-0.75 rounded-full text-base bg-transparent border border-dashed border-light-border dark:border-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-pointer font-sans hover:opacity-80">
              + {all.length - suggested.length} more
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const ContractRequestForm = () => {
  const navigate = useNavigate();
  const { service, addNotification, currentUser, isAdmin, contractTypes } = useApp();
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [skipDocGen, setSkipDocGen] = useState(false);
  const [splitPayment, setSplitPayment] = useState(false);
  const [createObligations, setCreateObligations] = useState(false);
  const [obligations, setObligations] = useState([]);
  const [newObligation, setNewObligation] = useState({
    title: "", category: "", responsibleParty: "", dueDate: "",
    priority: "High", riskLevel: "Medium"
  });
  const [ownerMappings, setOwnerMappings] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [form, setForm] = useState({
    categoryManager: currentUser?.displayName || "",
    effectiveDate: "", duration: "", entity: "", department: "",
    contractOwner: "", vendorName: "", supplierId: "", contractType: "", contractTitle: "",
    requestDate: new Date().toISOString().slice(0,10),
    isRenewal: "No", expiryDate: "", ceilingValueMonthly: "", ceilingValueAnnual: "",
    deviationFromPayment: "No", additionalComments: "",
    parentContractId: "", parentContractRef: "", manualRefNo: "",
    currency1: "USD", currency2: "NGN", splitPercent1: "50", splitAmount1: "", splitAmount2: "",
    procurementType: "",
    isReady: false,
  });

  // Load autosaved form data and owner mappings on mount
  useEffect(() => {
    const autosaved = getAutosavedFormData("contract-request");
    if (autosaved) {
      setForm(f => ({...f, ...autosaved}));
    }

    // Load owner mappings
    ownerMappingService.getMappings(service)
      .then(setOwnerMappings)
      .catch(e => console.warn("Failed to load owner mappings:", e))
      .finally(() => setLoadingOwners(false));
  }, []);

  // Autosave form on changes
  useFormAutosave("contract-request", form);
  const addFiles = (newFiles) => setFiles(prev => {
    const names = new Set(prev.map(f => f.name));
    return [...prev, ...Array.from(newFiles).filter(f => !names.has(f.name))];
  });
  const [contractSearch, setContractSearch] = useState("");
  const [allContracts, setAllContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);

  const set = (k,v) => setForm(p => ({...p,[k]:v}));

  // Load existing (non-addendum) contracts when Addendum type is selected
  const isAddendum = form.contractType && form.contractType.includes("Addendum");
  React.useEffect(() => {
    if (!isAddendum) { setAllContracts([]); setContractSearch(""); set("parentContractId",""); set("parentContractRef",""); return; }
    setLoadingContracts(true);
    service.getListItems("Contracts").then(d => {
      const list = (d?.value||[])
        .map(i => ({...i.fields, id: i.id}))
        .filter(c => !(c.ContractType && c.ContractType.includes("Addendum")));
      setAllContracts(list);
    }).catch(()=>setAllContracts([])).finally(()=>setLoadingContracts(false));
  }, [isAddendum]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["entity","department","vendorName","contractType","contractTitle","effectiveDate"];
    const missing = required.filter(k => !form[k]);
    if(missing.length) { addNotification("Please fill all required fields.","error"); return; }
    if(form.isRenewal === "Yes" && !form.duration) {
      addNotification("Duration is required for renewable contracts.","error"); return;
    }
    if(form.isRenewal === "Yes" && !form.expiryDate) {
      addNotification("Expiry Date is required for renewable contracts.","error"); return;
    }
    if(isAddendum && !form.parentContractId) {
      addNotification("Please select the parent contract this addendum belongs to.","error"); return;
    }
    if(skipDocGen && !isAddendum && !form.manualRefNo.trim()) {
      addNotification("Please enter the contract reference number.","error"); return;
    }
    if(!form.isReady) {
      addNotification("Please confirm that you have provided all the details for the contract to be completely drafted.","error"); return;
    }

    setSaving(true);
    try {
      const year = new Date().getFullYear();
      const month = new Date(form.effectiveDate).getMonth() + 1;

      // Ref number: manual input for existing contracts, addendum letter for addenda, auto-generate otherwise
      let refNo;
      if (skipDocGen && form.contractType !== "Addendum") {
        refNo = form.manualRefNo.trim();
      } else if (form.contractType === "Addendum") {
        const allData = await service.getListItems("Contracts").catch(()=>({value:[]}));
        const existingAddenda = (allData?.value||[]).filter(i => i.fields?.ParentContractId === form.parentContractId).length;
        refNo = generateAddendumRef(form.parentContractRef, existingAddenda);
      } else {
        const seq = await getNextSequence(service, form.entity, year);
        refNo = generateRefNumber(form.entity, year, seq);
      }

      const splitPercent2 = splitPayment ? String(100 - Number(form.splitPercent1)) : "";

      const fields = {
        Title: form.contractTitle, RefNo: refNo, Entity: form.entity,
        VendorName: form.vendorName, ContractType: form.contractType,
        CategoryManager: form.categoryManager, ContractOwner: form.contractOwner,
        Department: form.department, EffectiveDate: form.effectiveDate,
        Duration: form.duration, IsRenewal: form.isRenewal,
        ExpiryDate: form.expiryDate || null,
        CeilingValueMonthly: form.ceilingValueMonthly, CeilingValueAnnual: form.ceilingValueAnnual,
        DeviationFromPayment: form.deviationFromPayment, ProcurementType: form.procurementType, Comments: form.additionalComments,
        RequestDate: form.requestDate, Status: "Requested",
        RequestedBy: currentUser?.mail, Year: String(year), Month: String(month),
        IsReady: form.isReady === true ? "Yes" : "No",
        ...(form.contractType === "Addendum" && {
          ParentContractId: form.parentContractId,
          ParentContractRef: form.parentContractRef,
        }),
        ...(splitPayment && {
          SplitPayment: JSON.stringify({
            currency1: form.currency1, percent1: form.splitPercent1, amount1: form.splitAmount1,
            currency2: form.currency2, percent2: splitPercent2,     amount2: form.splitAmount2,
          }),
        }),
      };

      const item = await service.createListItem("Contracts", fields);

      let templateName = null;
      const driveData = await service.getDrives().catch(()=>null);
      if(driveData?.value) {
        const drive = driveData.value.find(d=>d.name==="ContractDocuments");
        if(drive) {
          if(form.contractType && form.contractType.includes("Addendum")) {
            // Addendum: reuse the parent contract's existing folder — no new folder created
            const parentContract = allContracts.find(c => c.id === form.parentContractId);
            const folderPath = parentContract
              ? buildFolderPath(parentContract.Entity, parentContract.Department, parentContract.Year || year, parentContract.Month || month, parentContract.RefNo)
              : buildFolderPath(form.entity, form.department, year, month, refNo);

            // Upload supporting docs into parent folder
            for(const file of files) {
              try {
                const buf = await file.arrayBuffer();
                await service.uploadFile(drive.id, folderPath, file.name, buf);
              } catch(ex) { console.warn("File upload failed:", ex); }
            }

            // Generate addendum draft from template (skipped if skipDocGen is on)
            templateName = !skipDocGen && await getTemplateName(service, "Addendum");
            if(templateName) {
              addNotification("Generating addendum draft from template...", "info");
              try {
                const safeRef = refNo.replace(/[/\\]/g, "-");
                const draft = await generateContractDraft({
                  service,
                  contractData: {
                    refNo,
                    contractTitle: form.contractTitle, contractType: form.contractType,
                    requestDate: form.requestDate, effectiveDate: form.effectiveDate,
                    expiryDate: form.expiryDate, duration: form.duration,
                    isRenewal: form.isRenewal, year: String(year), month: String(month),
                    entity: form.entity, department: form.department,
                    categoryManager: form.categoryManager,
                    contractOwner: form.contractOwner,
                    vendorName: form.vendorName,
                    ceilingValueMonthly: form.ceilingValueMonthly, ceilingValueAnnual: form.ceilingValueAnnual,
                    deviationFromPayment: form.deviationFromPayment,
                    parentContractRef: form.parentContractRef,
                    additionalComments: form.additionalComments,
                    requestedBy: currentUser?.mail || "",
                  },
                  folderPath,
                  contractId: item?.id || "",
                  uploadedBy: currentUser?.mail || "",
                  outputFileName: `ADDENDUM-${safeRef}.docx`,
                });
                if(draft) addNotification("Addendum draft saved: " + draft.fileName, "success");
              } catch(draftErr) {
                console.warn("Addendum draft generation failed:", draftErr);
                addNotification("Addendum saved. Draft could not be generated: " + draftErr.message, "warning");
              }
            }
          } else {
            // Standard contract: create new folder structure
            const folderPath = buildFolderPath(form.entity, form.department, year, month, refNo);
            const parts = folderPath.split("/");
            let cur = "";
            for(const part of parts) {
              try { await service.createFolder(drive.id, cur||null, part); } catch(ex) {}
              cur = cur ? cur+"/"+part : part;
            }

            // Upload supporting documents
            for(const file of files) {
              try {
                const buf = await file.arrayBuffer();
                await service.uploadFile(drive.id, folderPath, file.name, buf);
              } catch(ex) { console.warn("File upload failed:", ex); }
            }

            // Auto-generate pre-filled contract draft if an active mapping exists (skipped if skipDocGen is on)
            templateName = !skipDocGen && await getTemplateName(service, form.contractType);
            if(templateName) {
              addNotification("Generating contract draft from template...", "info");
              try {
                const draft = await generateContractDraft({
                  service,
                  contractData: {
                    refNo,
                    contractTitle: form.contractTitle, contractType: form.contractType,
                    requestDate: form.requestDate, effectiveDate: form.effectiveDate,
                    expiryDate: form.expiryDate, duration: form.duration,
                    isRenewal: form.isRenewal, year: String(year), month: String(month),
                    entity: form.entity, department: form.department,
                    categoryManager: form.categoryManager,
                    contractOwner: form.contractOwner,
                    vendorName: form.vendorName,
                    ceilingValueMonthly: form.ceilingValueMonthly, ceilingValueAnnual: form.ceilingValueAnnual,
                    deviationFromPayment: form.deviationFromPayment,
                    parentContractRef: form.parentContractRef,
                    additionalComments: form.additionalComments,
                    requestedBy: currentUser?.mail || "",
                  },
                  folderPath,
                  contractId: item?.id || "",
                  uploadedBy: currentUser?.mail || "",
                });
                if(draft) addNotification("Draft saved: " + draft.fileName, "success");
              } catch(draftErr) {
                console.warn("Template draft generation failed:", draftErr);
                addNotification("Contract saved. Template draft could not be generated: " + draftErr.message, "warning");
              }
            }
          }
        }
      }

      // Create initial obligations if enabled
      if (createObligations && obligations.length > 0) {
        for (const obl of obligations) {
          try {
            await createObligation(service, item?.id || "", {
              title: obl.title,
              description: "",
              category: obl.category,
              responsibleParty: obl.responsibleParty,
              responsibleEmail: currentUser?.mail || "",
              dueDate: obl.dueDate,
              priority: obl.priority,
              status: "Not Started",
              riskLevel: obl.riskLevel,
              contractRefNo: refNo,
            });
          } catch (oblErr) {
            console.warn("Failed to create obligation:", oblErr);
            addNotification("Contract created but some obligations failed: " + oblErr.message, "warning");
          }
        }
      }

      // Notify ALL admins (fetch all users, filter in-memory for role=admin to avoid OData filter issues)
      try {
        const allUsers = await service.getListItems("AllowedUsers");
        const adminEmails = (allUsers?.value || [])
          .filter(u => u.fields.Role === 'admin' || u.fields.Role === 'Admin')
          .map(u => u.fields.Title)
          .filter(email => email && email.trim());

        if (adminEmails.length > 0) {
          console.log(`[ContractRequestForm] Notifying ${adminEmails.length} admin(s):`, adminEmails);
          const html = buildEmailHTML("newRequest", {
            title: form.contractTitle, refNo, requestedBy: currentUser?.displayName,
            vendor: form.vendorName, type: form.contractType, entity: form.entity,
            portalUrl: window.location.origin + "/contracts/" + item?.id,
            templateNote: templateName ? " A pre-filled draft has been automatically created in the contract folder." : "",
          });
          await service.sendEmail(adminEmails, "[LexCore] New Contract Request: " + refNo, html)
            .then(() => console.log(`[ContractRequestForm] ✅ Notifications sent to ${adminEmails.length} admin(s)`))
            .catch(err => console.error(`[ContractRequestForm] ❌ Failed to send admin notifications:`, err));
        } else {
          console.warn("[ContractRequestForm] ⚠️ No admins found to notify");
        }
      } catch (adminNotifyErr) {
        console.error("[ContractRequestForm] ❌ Error fetching admins for notification:", adminNotifyErr);
      }

      // Clear autosaved data after successful submission
      clearAutosavedData("contract-request");

      addNotification("Contract request submitted! Ref: "+refNo,"success");
      navigate("/contracts/"+(item?.id||""));
    } catch(ex) {
      addNotification("Error submitting request: "+ex.message,"error");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-7">
        <h1 className="font-serif text-4xl font-medium text-light-text dark:text-dark-text mb-1">New Contract Request</h1>
        <p className="text-light-text-secondary dark:text-dark-text-secondary m-0">Complete the form below. All fields marked with * are required.</p>
        <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent my-2"/>
        {/* Skip document generation toggle — use for existing contracts being entered manually */}
        <div className={`inline-flex items-center gap-3 bg-light-bg-secondary dark:bg-dark-bg-secondary border rounded-2xl px-4.5 py-2.5 cursor-pointer select-none transition-all ${skipDocGen?"border-yellow-600 bg-yellow-600/5":"border-light-border dark:border-dark-border"}`} onClick={()=>setSkipDocGen(v=>!v)}>
          <div className={`w-9 h-5 rounded-full position-relative transition-colors flex-shrink-0 flex items-center ${skipDocGen?"bg-yellow-600":"bg-light-border dark:bg-dark-border"}`}>
            <div className={`w-3.5 h-3.5 rounded-full bg-light-text dark:bg-dark-text transition-transform ${skipDocGen?"translate-x-3.5":"translate-x-0.5"}`}/>
          </div>
          <div>
            <div className={`text-sm font-semibold ${skipDocGen?"text-accent":"text-light-text-secondary dark:text-dark-text-secondary"}`}>Skip document generation</div>
            <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.25">Enable when entering an existing contract that already has its documents</div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-6 mb-6">

          {/* Section 1: Parties */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-6 col-span-2">
            <div className="font-serif text-lg font-medium text-accent mb-4 pb-2 border-b border-light-border dark:border-dark-border">Party Information</div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Category Manager" required><Input value={form.categoryManager} onChange={e=>set("categoryManager",e.target.value)} /></Field>
              <Field label="Contracting Entity" required><Select options={ENTITIES} value={form.entity} onChange={e=>set("entity",e.target.value)}/></Field>
              <Field label="User Department" required>
                <select
                  value={form.department}
                  onChange={e => {
                    const dept = e.target.value;
                    set("department", dept);
                    // Auto-select contract owner based on department
                    const mapping = ownerMappings.find(m => m.department === dept);
                    if (mapping) {
                      set("contractOwner", mapping.owner);
                    } else {
                      set("contractOwner", "");
                    }
                  }}
                  className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-base outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors"
                >
                  <option value="">Select Department...</option>
                  {DEPARTMENTS.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </Field>
              <Field label="Contract Owner">
                <Input
                  value={form.contractOwner}
                  onChange={e=>set("contractOwner",e.target.value)}
                  placeholder="Auto-filled based on department"
                  disabled={!form.department}
                  className={!form.department ? "opacity-60 cursor-not-allowed" : ""}
                />
              </Field>
              <Field label="Supplier" required>
                <SupplierPicker
                  value={form.vendorName}
                  supplierId={form.supplierId}
                  onChange={({ id, name }) => { set("vendorName", name); set("supplierId", id); }}
                  required
                />
              </Field>
            </div>
          </div>

          {/* Section 2: Contract Details */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-6 col-span-2">
            <div className="font-serif text-lg font-medium text-accent mb-4 pb-2 border-b border-light-border dark:border-dark-border">Contract Details</div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Contract Type" required><Select options={CONTRACT_TYPES_NEW} value={form.procurementType} onChange={e=>set("procurementType",e.target.value)}/></Field>
              <Field label="Contract Title" required><Input value={form.contractTitle} onChange={e=>set("contractTitle",e.target.value)} placeholder="Descriptive contract title"/></Field>
              <Field label="Contract Template" required><Select options={contractTypes} value={form.contractType} onChange={e=>set("contractType",e.target.value)}/></Field>
              {skipDocGen && (!form.contractType || !form.contractType.includes("Addendum")) && (
                <Field label="Contract Reference No." required>
                  <Input value={form.manualRefNo} onChange={e=>set("manualRefNo",e.target.value)} placeholder="e.g. LEK/276/26-01"/>
                </Field>
              )}
              {form.contractType && form.contractType.includes("Addendum") && (
                <Field label="Parent Contract" required>
                  <div className="relative">
                    {form.parentContractRef ? (
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-yellow-600 rounded-lg">
                        <span className="font-mono text-sm text-accent">{form.parentContractRef}</span>
                        <span className="flex-1 text-sm text-light-text dark:text-dark-text overflow-hidden text-ellipsis whitespace-nowrap">
                          {allContracts.find(c=>c.id===form.parentContractId)?.Title || ""}
                        </span>
                        <button type="button" onClick={()=>{set("parentContractId","");set("parentContractRef","");setContractSearch("");}} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm p-0 hover:text-light-text dark:hover:text-dark-text">x</button>
                      </div>
                    ) : (
                      <>
                        <Input
                          value={contractSearch}
                          onChange={e=>setContractSearch(e.target.value)}
                          placeholder={loadingContracts ? "Loading contracts..." : "Search by ref no. or title..."}
                          disabled={loadingContracts}
                        />
                        {contractSearch.trim().length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg z-100 max-h-50 overflow-y-auto mt-1 shadow-2xl">
                            {allContracts
                              .filter(c => {
                                const q = contractSearch.toLowerCase();
                                const matchSearch = (c.RefNo||"").toLowerCase().includes(q) || (c.Title||"").toLowerCase().includes(q);
                                // Always narrow by supplier + entity when both are chosen
                                const matchSupplier = !form.vendorName || (c.VendorName||"").toLowerCase() === form.vendorName.toLowerCase();
                                const matchEntity   = !form.entity    || c.Entity === form.entity;
                                return matchSearch && matchSupplier && matchEntity;
                              })
                              .slice(0,10)
                              .map(c => (
                                <div key={c.id}
                                  onClick={()=>{ set("parentContractId",c.id); set("parentContractRef",c.RefNo||""); setContractSearch(""); }}
                                  className="px-3.5 py-2.5 cursor-pointer border-b border-light-border dark:border-dark-border flex items-center gap-2.5 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
                                  <span className="font-mono text-sm font-bold text-gray-900 flex-shrink-0">{c.RefNo}</span>
                                  <span className="text-sm text-light-text dark:text-dark-text overflow-hidden text-ellipsis whitespace-nowrap">{c.Title}</span>
                                  <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary flex-shrink-0 ml-auto">{c.VendorName}</span>
                                </div>
                              ))
                            }
                            {allContracts.filter(c=>{ const q=contractSearch.toLowerCase(); return (c.RefNo||"").toLowerCase().includes(q)||(c.Title||"").toLowerCase().includes(q); }).length === 0 && (
                              <div className="px-3.5 py-2.5 text-sm text-light-text-secondary dark:text-dark-text-secondary">No contracts found.</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                    The addendum document will be stored in this contract's existing folder.
                    {(form.vendorName||form.entity) && (
                      <span className="text-accent ml-1">
                        Showing contracts for{form.vendorName ? ` ${form.vendorName}` : ""}{form.entity ? ` · ${form.entity}` : ""}.
                      </span>
                    )}
                  </div>
                </Field>
              )}
              <Field label="Date of Request" required><Input type="date" value={form.requestDate} min={new Date().toISOString().slice(0,10)} onChange={e=>set("requestDate",e.target.value)}/></Field>
              <Field label="Effective Date" required><Input type="date" value={form.effectiveDate} onChange={e=>set("effectiveDate",e.target.value)}/></Field>
              <Field label="Duration (e.g. 12 months)" required={form.isRenewal==="Yes"}><Input value={form.duration} onChange={e=>set("duration",e.target.value)} placeholder="e.g. 24 months"/></Field>
              <Field label="Expiry Date" required={form.isRenewal==="Yes"}><Input type="date" value={form.expiryDate} onChange={e=>set("expiryDate",e.target.value)} style={form.isRenewal==="Yes"&&!form.expiryDate?{borderColor:"#c9a84c55"}:{}}/></Field>
              <Field label="Renewable">
                <div style={{display:"flex",gap:16,paddingTop:10}}>
                  {["Yes","No"].map(v=>(
                    <label key={v} style={{display:"flex",alignItems:"center",gap:6,color:"#c8c4bc",cursor:"pointer",fontSize:15}}>
                      <input type="radio" name="isRenewal" value={v} checked={form.isRenewal===v} onChange={()=>set("isRenewal",v)} style={{accentColor:"#c9a84c"}}/>
                      {v}
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </div>

          {/* Section 3: Financial */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-6">
            <div className="font-serif text-lg font-medium text-accent mb-4 pb-2 border-b border-light-border dark:border-dark-border">Financial Terms</div>
            <Field label="Ceiling Value (Monthly)"><Input value={form.ceilingValueMonthly} onChange={e=>set("ceilingValueMonthly",e.target.value)} placeholder="e.g. \,000 / month"/></Field>
            <Field label="Ceiling Value (Annual)"><Input value={form.ceilingValueAnnual} onChange={e=>set("ceilingValueAnnual",e.target.value)} placeholder="e.g.  ,000 / year"/></Field>
            <Field label="Deviation from Standard Payment Terms">
              <div style={{display:"flex",gap:16,paddingTop:10}}>
                {["Yes","No"].map(v=>(
                  <label key={v} style={{display:"flex",alignItems:"center",gap:6,color:"#c8c4bc",cursor:"pointer",fontSize:15}}>
                    <input type="radio" name="deviation" value={v} checked={form.deviationFromPayment===v} onChange={()=>set("deviationFromPayment",v)} style={{accentColor:"#c9a84c"}}/>
                    {v}
                  </label>
                ))}
              </div>
            </Field>

            {/* Split Currency Payment */}
            <div className={`mt-4.5 border-t border-light-border dark:border-dark-border pt-4 ${splitPayment?"":"pb-0"}`}>
              <div className={`flex items-center justify-between ${splitPayment?"mb-4":"mb-0"}`}>
                <div className="text-sm font-semibold text-light-text-secondary dark:text-dark-text-secondary">Split Currency Payment</div>
                <div className="flex items-center gap-2.5 cursor-pointer" onClick={()=>setSplitPayment(v=>!v)}>
                  <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{splitPayment?"Enabled":"Disabled"}</span>
                  <div className={`w-9 h-5 rounded-full position-relative transition-colors flex-shrink-0 flex items-center ${splitPayment?"bg-yellow-600":"bg-light-border dark:bg-dark-border"}`}>
                    <div className={`w-3.5 h-3.5 rounded-full bg-light-text dark:bg-dark-text transition-transform ${splitPayment?"translate-x-3.5":"translate-x-0.5"}`}/>
                  </div>
                </div>
              </div>
              {splitPayment && (
                <div className="flex flex-col gap-3">
                  <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg p-3.5 border border-light-border dark:border-dark-border">
                    <div className="text-xs text-accent font-semibold mb-2.5">Currency 1</div>
                    <div className="grid grid-cols-[1fr_80px_1fr] gap-2.5 items-end">
                      <Field label="Currency">
                        <select value={form.currency1} onChange={e=>set("currency1",e.target.value)} className="w-full px-3 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-base outline-none focus:ring-1 focus:ring-accent">
                          {CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Split %">
                        <Input type="number" min="0" max="100" value={form.splitPercent1} onChange={e=>set("splitPercent1",e.target.value)} placeholder="50"/>
                      </Field>
                      <Field label="Amount">
                        <Input value={form.splitAmount1} onChange={e=>set("splitAmount1",e.target.value)} placeholder="e.g. 250,000"/>
                      </Field>
                    </div>
                  </div>
                  <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg p-3.5 border border-light-border dark:border-dark-border">
                    <div className="text-xs text-accent font-semibold mb-2.5">Currency 2</div>
                    <div className="grid grid-cols-[1fr_80px_1fr] gap-2.5 items-end">
                      <Field label="Currency">
                        <select value={form.currency2} onChange={e=>set("currency2",e.target.value)} className="w-full px-3 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-base outline-none focus:ring-1 focus:ring-accent">
                          {CURRENCIES.map(c=><option key={c} value={c}>{c}</option>)}
                        </select>
                      </Field>
                      <Field label="Split % (auto)">
                        <input readOnly value={form.splitPercent1 ? String(100 - Number(form.splitPercent1)) : ""} className="w-full px-3 py-2.5 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary text-base outline-none cursor-not-allowed opacity-60"/>
                      </Field>
                      <Field label="Amount">
                        <Input value={form.splitAmount2} onChange={e=>set("splitAmount2",e.target.value)} placeholder="e.g. 150,000"/>
                      </Field>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 4: Supporting Docs + Comments */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-6">
            <div className="font-serif text-lg font-medium text-accent mb-4 pb-2 border-b border-light-border dark:border-dark-border">Supporting Documents</div>

            {/* Document type guide */}
            <DocTypeGuide contractType={form.contractType}/>

            <div
              onDragOver={e=>{e.preventDefault();setDrag(true);}}
              onDragEnter={e=>{e.preventDefault();setDrag(true);}}
              onDragLeave={e=>{e.preventDefault();setDrag(false);}}
              onDrop={e=>{e.preventDefault();setDrag(false);addFiles(e.dataTransfer.files);}}
              onClick={()=>document.getElementById("suppDocs").click()}
              className={`border-2 border-dashed rounded-lg p-5 text-center mb-4 cursor-pointer transition-all ${
                drag ? "border-yellow-600 bg-yellow-600/5" : "border-light-border dark:border-dark-border bg-transparent"
              }`}>
              <input type="file" multiple id="suppDocs" className="hidden" onChange={e=>addFiles(e.target.files)} accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg"/>
              <div className="text-2xl mb-1.5 pointer-events-none">📎</div>
              <div className={`text-sm pointer-events-none ${drag?"text-yellow-600":"text-light-text-secondary dark:text-dark-text-secondary"}`}>
                {drag ? "Drop files here" : "Click to browse or drag files here"}
              </div>
              <div className="text-xs text-light-border dark:text-dark-border mt-0.75 pointer-events-none">PDF, DOCX, XLSX, Images accepted</div>
            </div>
            {files.length > 0 && files.map((f,i)=>(
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">
                <span>📄</span><span className="flex-1 text-sm">{f.name}</span>
                <button type="button" onClick={e=>{e.stopPropagation();setFiles(prev=>prev.filter((_,j)=>j!==i));}} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:text-light-text dark:hover:text-dark-text">×</button>
              </div>
            ))}
            <Field label="Additional Information / Comments">
              <textarea value={form.additionalComments} onChange={e=>set("additionalComments",e.target.value)} rows={4} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-base outline-none focus:ring-1 focus:ring-accent resize-vertical" placeholder="Any additional notes or special instructions..."/>
            </Field>
          </div>

          {/* Section 6: Initial Obligations (Optional) */}
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-6 col-span-2">
            <div className="flex items-start gap-3 mb-4 pb-2 border-b border-light-border dark:border-dark-border">
              <div>
                <div className="font-serif text-lg font-medium text-accent">Initial Obligations</div>
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">Optional: Create contract obligations right now or add them later</div>
              </div>
            </div>

            {/* Toggle to enable obligation creation */}
            <div className={`inline-flex items-center gap-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border rounded-2xl px-3 py-2 cursor-pointer select-none transition-all mb-4 ${createObligations?"border-yellow-600 bg-yellow-600/5":"border-light-border dark:border-dark-border"}`} onClick={()=>setCreateObligations(v=>!v)}>
              <div className={`w-8 h-4.5 rounded-full position-relative transition-colors flex-shrink-0 flex items-center ${createObligations?"bg-yellow-600":"bg-light-border dark:bg-dark-border"}`}>
                <div className={`w-3 h-3 rounded-full bg-light-text dark:bg-dark-text transition-transform ${createObligations?"translate-x-3.5":"translate-x-0.5"}`}/>
              </div>
              <span className={`text-sm font-medium ${createObligations?"text-accent":"text-light-text-secondary dark:text-dark-text-secondary"}`}>Add obligations now</span>
            </div>

            {createObligations && (
              <div>
                {/* Add obligation form */}
                <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg p-4 mb-4 border border-light-border dark:border-dark-border">
                  <div className="grid grid-cols-[2fr_1fr] gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1 font-medium">Obligation Title</label>
                      <Input value={newObligation.title} onChange={e=>setNewObligation(p=>({...p,title:e.target.value}))} placeholder="e.g., Submit Q1 Report"/>
                    </div>
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1 font-medium">Category</label>
                      <Select options={OBLIGATION_CATEGORIES} value={newObligation.category} onChange={e=>setNewObligation(p=>({...p,category:e.target.value}))}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1 font-medium">Responsible Party</label>
                      <Input value={newObligation.responsibleParty} onChange={e=>setNewObligation(p=>({...p,responsibleParty:e.target.value}))} placeholder="e.g., Finance Team"/>
                    </div>
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1 font-medium">Due Date</label>
                      <Input type="date" value={newObligation.dueDate} onChange={e=>setNewObligation(p=>({...p,dueDate:e.target.value}))}/>
                    </div>
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1 font-medium">Priority</label>
                      <Select options={OBLIGATION_PRIORITIES} value={newObligation.priority} onChange={e=>setNewObligation(p=>({...p,priority:e.target.value}))}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1 font-medium">Risk Level</label>
                      <Select options={RISK_LEVELS} value={newObligation.riskLevel} onChange={e=>setNewObligation(p=>({...p,riskLevel:e.target.value}))}/>
                    </div>
                    <div className="flex items-end">
                      <button type="button" onClick={()=>{
                        if(!newObligation.title||!newObligation.category||!newObligation.responsibleParty||!newObligation.dueDate) {
                          addNotification("Please fill all obligation fields","error");
                          return;
                        }
                        setObligations([...obligations,newObligation]);
                        setNewObligation({title:"",category:"",responsibleParty:"",dueDate:"",priority:"High",riskLevel:"Medium"});
                      }} className="w-full px-4 py-2.5 bg-yellow-600 border-none rounded-lg text-slate-900 cursor-pointer font-semibold text-sm hover:bg-yellow-700 transition-colors">
                        + Add Obligation
                      </button>
                    </div>
                  </div>
                </div>

                {/* List of added obligations */}
                {obligations.length > 0 && (
                  <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg p-4 border border-light-border dark:border-dark-border">
                    <div className="text-sm font-semibold text-accent mb-3">Added Obligations ({obligations.length})</div>
                    <div className="flex flex-col gap-2">
                      {obligations.map((obl,i)=>(
                        <div key={i} className="flex items-center justify-between px-2.5 py-2 bg-light-bg-secondary dark:bg-dark-bg-secondary rounded border border-light-border dark:border-dark-border">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-light-text dark:text-dark-text">{obl.title}</div>
                            <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">
                              {obl.category} • {obl.responsibleParty} • {new Date(obl.dueDate).toLocaleDateString("en-GB")} • {obl.priority}
                            </div>
                          </div>
                          <button type="button" onClick={()=>setObligations(obligations.filter((_,j)=>j!==i))} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-lg px-2 hover:text-red-500">×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 justify-between items-center">
          <label className="flex items-center gap-2 text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.isReady || false}
              onChange={e=>set("isReady",e.target.checked)}
              className="w-4.5 h-4.5 cursor-pointer accent-accent"
            />
            <span>✅ Kindly check this box if you have provided all the details for the contract to be completely drafted</span>
          </label>
          <div className="flex gap-3">
            <button type="button" onClick={()=>navigate("/contracts")} className="px-6 py-2.75 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className={`px-7 py-2.75 border-none rounded-lg text-slate-900 cursor-pointer text-sm font-semibold transition-colors ${saving?"bg-yellow-700 cursor-not-allowed opacity-75":"bg-yellow-600 hover:bg-yellow-700"}`}>
              {saving ? "Submitting..." : "Submit Contract Request"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
